/**
 * Image card component
 */

import { useState } from 'react';
import type { Media } from '../../../../packages/shared/src/types';

interface ImageCardProps {
  image: Media;
  onDelete?: (id: string) => void;
  onShare?: (id: string) => void;
  onClick?: (id: string) => void;
}

export function ImageCard({ image, onDelete, onShare, onClick }: ImageCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete) return;
    
    setIsDeleting(true);
    await onDelete(image.id);
    setIsDeleting(false);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onShare) {
      onShare(image.id);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div 
      className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden hover:border-indigo-500 transition-colors cursor-pointer"
      onClick={() => onClick?.(image.id)}
    >
      <div className="aspect-video bg-gray-900 relative overflow-hidden">
        <img
          src={image.url}
          alt={image.originalName}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="%236b7280"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /%3E%3C/svg%3E';
          }}
        />
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 text-xs font-medium rounded ${
            image.status === 'active' ? 'bg-green-900/80 text-green-300' :
            image.status === 'archived' ? 'bg-yellow-900/80 text-yellow-300' :
            'bg-red-900/80 text-red-300'
          }`}>
            {image.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-white truncate" title={image.originalName}>
          {image.originalName}
        </h3>
        <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
          <span>{formatFileSize(image.size)}</span>
          <span>{formatDate(image.createdAt)}</span>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={handleShare}
            className="flex-1 px-3 py-2 text-xs font-medium text-indigo-300 bg-indigo-900/50 hover:bg-indigo-900 rounded transition-colors"
          >
            Share URL
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-3 py-2 text-xs font-medium text-red-300 bg-red-900/50 hover:bg-red-900 rounded transition-colors disabled:opacity-50"
          >
            {isDeleting ? '...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageCard;